import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Activity, KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  // Phase H — when the server replies "Two-factor code required" we flip
  // this on so the form reveals the OTP field. We keep username + password
  // so the user only has to type the code.
  const [otpRequired, setOtpRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    const to = user.scope === "platform" ? "/platform" : "/";
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(username, password, otpCode || undefined);
      // Hotfix Z.19: no success toast on login. The redirect to dashboard /
      // platform shell is signal enough that auth worked, and the toast
      // duplicated information the user already sees. Errors still toast.
      navigate(r.scope === "platform" ? "/platform" : "/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      // Surface the 2FA challenge step
      if (
        err?.response?.status === 401 &&
        /two-factor|2fa|otp/i.test(detail)
      ) {
        setOtpRequired(true);
        // Toast only on the first prompt; subsequent wrong codes get a fresh toast
        if (!otpCode) {
          toast.message(t("login.otpRequired", "Enter your 2FA code to continue"));
        } else {
          toast.error(t("login.otpInvalid", "Invalid 2FA code, try again."));
        }
        setOtpCode("");
      } else {
        toast.error(detail || t("login.invalidCredentials"));
        setOtpRequired(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-card border-r border-border relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-80 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(1000px circle at 10% 0%, hsl(var(--accent)/0.18), transparent 55%), radial-gradient(800px circle at 80% 100%, hsl(var(--info)/0.10), transparent 60%)",
          }}
        />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-md bg-accent/15 grid place-items-center">
              <Activity className="h-6 w-6 text-accent" />
            </div>
            <div>
              <div className="font-semibold tracking-tight text-lg text-foreground">{t("login.title")}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t("common.appTagline")}
              </div>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
        <div className="relative max-w-md">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground">
            {t("login.subtitle")}
          </h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            {t("login.adminOrTenantOwner")}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Domain-scoped auth", "IP ACLs", "CPS limiter", "Channel quotas", "Live CDRs"].map(
              (tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background/60 px-2 py-1 text-[11px] font-medium text-muted-foreground"
                >
                  <ShieldCheck className="h-3 w-3 text-accent" /> {tag}
                </span>
              )
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm space-y-4 bg-card border border-border rounded-lg p-6"
          data-testid="login-form"
        >
          <div className="flex items-center justify-between lg:hidden">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {t("login.signIn")}
            </h1>
            <LanguageSwitcher />
          </div>
          <div className="hidden lg:block">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">{t("login.signIn")}</h1>
          </div>
          <div>
            <Label htmlFor="u">{t("login.emailOrUsername")}</Label>
            <Input
              id="u"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              placeholder="admin  or  owner@your.com"
              data-testid="login-username"
              required
            />
          </div>
          <div>
            <Label htmlFor="p">{t("login.password")}</Label>
            <PasswordInput
              id="p"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="login-password"
              required
            />
          </div>
          {otpRequired && (
            <div data-testid="login-otp-block">
              <Label htmlFor="otp" className="flex items-center gap-1">
                <KeyRound className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                {t("login.otpLabel", "Two-factor code")}
              </Label>
              <Input
                id="otp"
                inputMode="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.trim())}
                placeholder="000000  or recovery code"
                className="font-mono"
                autoFocus
                data-testid="login-otp"
                required
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {t(
                  "login.otpHint",
                  "Open your authenticator app, or use a recovery code if you've lost it."
                )}
              </p>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading ? t("common.loading") : t("login.signIn")}
          </Button>
          <div className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
            <a href="/signup" className="text-accent hover:underline" data-testid="goto-signup">
              {t("login.signUp")}
            </a>
          </div>
          <div className="flex items-center justify-center gap-3 text-[11px] text-muted-foreground" data-testid="login-legal-links">
            <a href="/terms" className="hover:text-foreground" data-testid="login-link-terms">
              {t("legal.termsTitle", "Terms of Service")}
            </a>
            <span aria-hidden>·</span>
            <a href="/privacy" className="hover:text-foreground" data-testid="login-link-privacy">
              {t("legal.privacyTitle", "Privacy Policy")}
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
